import { useState, useRef, useEffect, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { setChatInProgress } from './chatState';

const API_URL = (process.env.REACT_APP_API_URL ?? '').trim().replace(/\/+$/, '');

// Model preference storage (still using localStorage for this as it's just a UI preference)
const getModelKey = (template) => `chat_model_${template}`;

// Get or set model preference (per template) - still using localStorage for UI preference
const getModelPreference = (template) => {
  const key = getModelKey(template);
  const stored = localStorage.getItem(key);
  return stored || 'openai'; // Default to GPT 4.1
};

const setModelPreference = (template, model) => {
  const key = getModelKey(template);
  localStorage.setItem(key, model);
};

// Load messages from backend API (per template and model)
const loadMessagesFromStorage = async (template, model, getAuthHeaders) => {
  try {
    const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
    const response = await fetch(`${API_URL}/chat/history?template=${encodeURIComponent(template)}&model=${encodeURIComponent(model)}`, { headers: authHeaders });
    
    if (!response.ok) {
      throw new Error(`Failed to load chat history: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.messages && Array.isArray(data.messages) && data.messages.length > 0) {
      console.log(`[ChatAdvisor] Found ${data.messages.length} stored messages for ${template}/${model}`);
      return { messages: data.messages, threadId: data.thread_id || null };
    }
    
    console.log(`[ChatAdvisor] No stored messages found for ${template}/${model}, starting fresh`);
    return { 
      messages: [{ role: 'advisor', text: 'How can I help with today\'s huddle board?' }], 
      threadId: null 
    };
  } catch (err) {
    console.error('Error loading chat messages from backend:', err);
    // Return default on error
    return { 
      messages: [{ role: 'advisor', text: 'How can I help with today\'s huddle board?' }], 
      threadId: null 
    };
  }
};

// Save messages to backend API (per template and model)
const saveMessagesToStorage = async (messages, template, model, threadId = null, getAuthHeaders) => {
  try {
    const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
    const response = await fetch(`${API_URL}/chat/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        template,
        model,
        messages,
        thread_id: threadId
      }),
    });
    
    if (!response.ok) {
      throw new Error(`Failed to save chat history: ${response.status}`);
    }
    
    const data = await response.json();
    if (!data.success) {
      throw new Error(data.error || 'Failed to save chat history');
    }
    
    console.log(`[ChatAdvisor] Saved ${messages.length} messages to backend for ${template}/${model}`);
  } catch (err) {
    console.error('Error saving chat messages to backend:', err);
  }
};

// Generate or retrieve thread_id - now stored in backend
const getThreadId = async (template, model, existingThreadId = null) => {
  if (existingThreadId) {
    return existingThreadId;
  }
  // Generate a simple unique ID
  const newThreadId = `thread_${template}_${model}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return newThreadId;
};

// Helper: detect default single advisor message
const isDefaultMessages = (msgs = []) => {
  return msgs.length === 1 &&
    msgs[0]?.role === 'advisor' &&
    typeof msgs[0]?.text === 'string' &&
    msgs[0].text.includes('How can I help with today\'s huddle board?');
};


// Export clear function for use in App.js when creating new templates or deleting them
export const clearTemplateChatHistory = async (template, getAuthHeaders) => {
  try {
    const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
    // Delete all chat history for this template (all models)
    const response = await fetch(`${API_URL}/chat/history?template=${encodeURIComponent(template)}`, {
      method: 'DELETE',
      headers: { ...authHeaders },
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`[ChatAdvisor]  Cleared chat history for ${template}:`, data.message || 'Success');
    } else {
      console.log(`[ChatAdvisor] No chat history found to clear for ${template} (already clean)`);
    }
    
    // Trigger a custom event for ChatAdvisor to react
    window.dispatchEvent(new CustomEvent('chatHistoryCleared', { detail: { template } }));
  } catch (err) {
    console.error('Error clearing chat history:', err);
  }
};

function ChatAdvisor({ selectedTemplate = 'template-1', isOwner = true, getAuthHeaders }) {
  // Get model preference for this template
  const [selectedModel, setSelectedModel] = useState(() => getModelPreference(selectedTemplate));
  
  // Track current context to prevent cross-contamination
  const currentContextRef = useRef({ template: selectedTemplate, model: getModelPreference(selectedTemplate) });
  
  // Initialize with default messages, will load from API in useEffect
  const [messages, setMessages] = useState([{ role: 'advisor', text: 'How can I help with today\'s huddle board?' }]);
  const [draft, setDraft] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFilePath, setUploadedFilePath] = useState(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const chatBodyRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const csvExcelInputRef = useRef(null);
  const attachmentMenuRef = useRef(null);
  const threadIdRef = useRef(null);
  const isMountedRef = useRef(true);
  const prevTemplateRef = useRef(selectedTemplate);
  const prevModelRef = useRef(selectedModel);
  const messagesRef = useRef(messages);
  const isSwitchingRef = useRef(false); // Flag to prevent operations during switch
  
  // Track mount/unmount to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!showAttachmentMenu) return;
    const handleClickOutside = (event) => {
      if (!attachmentMenuRef.current?.contains(event.target)) {
        setShowAttachmentMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAttachmentMenu]);

  // Initial load of chat history from backend
  useEffect(() => {
    const loadInitialHistory = async () => {
      const model = getModelPreference(selectedTemplate);
      setIsLoadingHistory(true);
      try {
        const result = await loadMessagesFromStorage(selectedTemplate, model, getAuthHeaders);
        if (isMountedRef.current) {
          setMessages(result.messages);
          messagesRef.current = result.messages;
          threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, model);
          currentContextRef.current = { template: selectedTemplate, model };
          console.log(`[ChatAdvisor] Initial load for template: ${selectedTemplate}, model: ${model}, messages: ${result.messages.length}`);
        }
      } catch (err) {
        console.error('Error loading initial chat history:', err);
      } finally {
        if (isMountedRef.current) {
          setIsLoadingHistory(false);
        }
      }
    };
    
    loadInitialHistory();
  }, []); // Only run on mount

  // Listen for chat history cleared events
  useEffect(() => {
    const handleChatHistoryCleared = async (event) => {
      const clearedTemplate = event.detail?.template;
      // If the cleared template is the current one, reload messages to ensure fresh state
      if (clearedTemplate === selectedTemplate) {
        console.log(`[ChatAdvisor] Chat history was cleared for current template ${selectedTemplate}, reloading...`);
        try {
          const result = await loadMessagesFromStorage(selectedTemplate, selectedModel, getAuthHeaders);
          if (isMountedRef.current) {
            setMessages(result.messages);
            messagesRef.current = result.messages;
            threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, selectedModel);
          }
        } catch (err) {
          console.error('Error reloading chat history:', err);
        }
      }
    };

    window.addEventListener('chatHistoryCleared', handleChatHistoryCleared);
    return () => {
      window.removeEventListener('chatHistoryCleared', handleChatHistoryCleared);
    };
  }, [selectedTemplate, selectedModel]);

  // Keep messagesRef in sync with messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Handle template or model changes - load appropriate chat history
  useEffect(() => {
    const switchChatHistory = async () => {
      const templateChanged = prevTemplateRef.current !== selectedTemplate;
      const modelChanged = prevModelRef.current !== selectedModel;
      
      // If nothing changed, skip
      if (!templateChanged && !modelChanged) {
        return;
      }
      
      // Set switching flag to prevent other operations
      isSwitchingRef.current = true;
      
      // Determine which model to use
      let modelToUse = selectedModel;
      if (templateChanged) {
        // Template changed - get model preference for new template
        modelToUse = getModelPreference(selectedTemplate);
        // Update model state if it's different
        if (modelToUse !== selectedModel) {
          setSelectedModel(modelToUse);
          // Don't proceed yet - let this effect run again with the new model
          isSwitchingRef.current = false;
          return;
        }
      }
      
      // If template or model changed, switch chat history
      console.log(`[ChatAdvisor] Template/Model changed: ${prevTemplateRef.current}/${prevModelRef.current} -> ${selectedTemplate}/${modelToUse}`);
      
      // Save current messages before switching (only if we have valid previous context)
      if (prevTemplateRef.current && prevModelRef.current && messagesRef.current.length > 0 && !isDefaultMessages(messagesRef.current)) {
        console.log(`[ChatAdvisor] Saving ${messagesRef.current.length} messages for ${prevTemplateRef.current}/${prevModelRef.current}`);
        await saveMessagesToStorage(
          messagesRef.current,
          prevTemplateRef.current,
          prevModelRef.current,
          threadIdRef.current,
          getAuthHeaders
        );
      }
      
      // Load messages for new template/model combination
      try {
        const result = await loadMessagesFromStorage(selectedTemplate, modelToUse, getAuthHeaders);
        console.log(`[ChatAdvisor] Loading ${result.messages.length} messages for ${selectedTemplate}/${modelToUse}`);
        
        // Update context ref BEFORE setting state
        currentContextRef.current = { template: selectedTemplate, model: modelToUse };
        
        // Update state
        if (isMountedRef.current) {
          setMessages(result.messages);
          messagesRef.current = result.messages;
          
          // Update thread_id for new template/model
          threadIdRef.current = result.threadId || await getThreadId(selectedTemplate, modelToUse);
          console.log(`[ChatAdvisor] Thread ID updated to: ${threadIdRef.current}`);
        }
      } catch (err) {
        console.error('Error loading chat history:', err);
      }
      
      // Clear draft, image, and file when switching templates
      if (templateChanged) {
        setDraft('');
        setSelectedImage(null);
        setImagePreview(null);
        setSelectedFile(null);
        setUploadedFilePath(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        if (csvExcelInputRef.current) {
          csvExcelInputRef.current.value = '';
        }
      }
      
      // Update refs
      prevTemplateRef.current = selectedTemplate;
      prevModelRef.current = modelToUse;
      
      // Clear switching flag after a brief delay to ensure state is settled
      setTimeout(() => {
        isSwitchingRef.current = false;
      }, 100);
    };
    
    switchChatHistory();
  }, [selectedTemplate, selectedModel]); // Depend on both template and model
  
  // Handle model change
  const handleModelChange = async (newModel) => {
    console.log(`[ChatAdvisor] Model change requested: ${selectedModel} -> ${newModel} for template: ${selectedTemplate}`);
    
    // Save current messages before switching models
    if (messagesRef.current.length > 0 && !isDefaultMessages(messagesRef.current)) {
      console.log(`[ChatAdvisor] Saving ${messagesRef.current.length} messages before model switch`);
      await saveMessagesToStorage(
        messagesRef.current,
        selectedTemplate,
        selectedModel,
        threadIdRef.current,
        getAuthHeaders
      );
    }
    
    setSelectedModel(newModel);
    setModelPreference(selectedTemplate, newModel);
    
    // The useEffect will handle loading the new model's chat history
  };

  // Save messages to backend whenever they change (backup)
  useEffect(() => {
    // Don't save during template/model switch or initial load
    if (isSwitchingRef.current || isLoadingHistory) {
      return;
    }
    
    // Validate context matches before saving
    if (currentContextRef.current.template !== selectedTemplate || 
        currentContextRef.current.model !== selectedModel) {
      console.log(`[ChatAdvisor]  Context mismatch during save, skipping. Current: ${currentContextRef.current.template}/${currentContextRef.current.model}, Expected: ${selectedTemplate}/${selectedModel}`);
      return;
    }
    
    if (messages.length > 0 && !isDefaultMessages(messages)) {
      console.log(`[ChatAdvisor] Auto-saving ${messages.length} messages for ${selectedTemplate}/${selectedModel}`);
      saveMessagesToStorage(messages, selectedTemplate, selectedModel, threadIdRef.current, getAuthHeaders);
    }
  }, [messages, selectedTemplate, selectedModel, isLoadingHistory]);

  // Save messages before component unmounts (safety net)
  useEffect(() => {
    return () => {
      // Save messages on unmount as a safety net
      if (messages.length > 0 && !isSwitchingRef.current && !isDefaultMessages(messages)) {
        saveMessagesToStorage(messages, selectedTemplate, selectedModel, threadIdRef.current, getAuthHeaders);
      }
    };
  }, [messages, selectedTemplate, selectedModel]);

  // Safety check: If messages seem to have been lost, reload from backend
  // BUT: Only restore if we're sure it's the right template/model combination
  useEffect(() => {
    // Don't check during switch or initial load
    if (isSwitchingRef.current || isLoadingHistory) {
      return;
    }
    
    // Validate context matches
    if (currentContextRef.current.template !== selectedTemplate || 
        currentContextRef.current.model !== selectedModel) {
      return;
    }
    
    // Check if we have fewer messages than what's in backend (might have been reset)
    const checkBackendHistory = async () => {
      try {
        const result = await loadMessagesFromStorage(selectedTemplate, selectedModel, getAuthHeaders);
        // Only restore if:
        // 1. Backend has more messages
        // 2. Current messages is just the default message (1 message)
        // 3. Context refs match (we're in the right state)
        const isDefaultMessage = messages.length === 1 && 
                                 messages[0]?.role === 'advisor' && 
                                 messages[0]?.text?.includes('How can I help');
        const isCorrectContext = currentContextRef.current.template === selectedTemplate && 
                                 currentContextRef.current.model === selectedModel &&
                                 prevTemplateRef.current === selectedTemplate && 
                                 prevModelRef.current === selectedModel;
        
        if (result.messages.length > messages.length && isDefaultMessage && isCorrectContext) {
          // We seem to have lost messages, restore from backend
          console.log(`[ChatAdvisor]  Messages lost, restoring from backend. Backend has: ${result.messages.length}, Current has: ${messages.length}`);
          if (isMountedRef.current) {
            setMessages(result.messages);
            messagesRef.current = result.messages;
            if (result.threadId) {
              threadIdRef.current = result.threadId;
            }
          }
        }
      } catch (e) {
        // Ignore errors
        console.error('Error checking backend history:', e);
      }
    };
    
    checkBackendHistory();
  }, [messages.length, selectedTemplate, selectedModel, isLoadingHistory]); // Only check when message count changes

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [draft]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Image size must be less than 10MB');
        return;
      }
      
      setSelectedImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const fileExt = file.name.toLowerCase().split('.').pop();
      if (!['csv', 'xlsx', 'xls'].includes(fileExt)) {
        alert('Please select a CSV or Excel file (.csv, .xlsx, .xls)');
        return;
      }
      
      // Validate file size (max 50MB for data files)
      if (file.size > 50 * 1024 * 1024) {
        alert('File size must be less than 50MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Upload the file immediately
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData,
        });
        
        const data = await response.json();
        if (data.success) {
          setUploadedFilePath(data.file_path);
          console.log(`File uploaded: ${data.file_path}`);
        } else {
          alert(`Upload failed: ${data.error || 'Unknown error'}`);
          setSelectedFile(null);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        alert('Failed to upload file. Please try again.');
        setSelectedFile(null);
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setUploadedFilePath(null);
    if (csvExcelInputRef.current) {
      csvExcelInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix (data:image/png;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const sendMessage = async () => {
    if ((!draft.trim() && !selectedImage && !uploadedFilePath) || isLoading) return;
    
    // Check ownership before allowing message
    if (!isOwner) {
      alert('You do not have permission to edit this board. Please copy it first to create your own editable version.');
      return;
    }
    
    // Validate context before sending
    if (isSwitchingRef.current) {
      console.log(`[ChatAdvisor]  Cannot send message during template/model switch`);
      return;
    }
    
    if (currentContextRef.current.template !== selectedTemplate || 
        currentContextRef.current.model !== selectedModel) {
      console.log(`[ChatAdvisor]  Context mismatch, cannot send. Current: ${currentContextRef.current.template}/${currentContextRef.current.model}, Expected: ${selectedTemplate}/${selectedModel}`);
      return;
    }
    
    // Save image and file references before clearing
    const imageToSend = selectedImage;
    const imagePreviewToShow = imagePreview;
    const filePathToSend = uploadedFilePath;
    const draftMessage = draft.trim();
    const userMessage = draftMessage || (imageToSend ? 'Describe this image' : uploadedFilePath ? 'Analyze this file' : '');
    const userMsgObj = { 
      role: 'user', 
      text: userMessage, 
      image: imagePreviewToShow || null,
      file: filePathToSend ? ` ${selectedFile?.name || 'File'}` : null
    };
    
    // Mark chat as in progress FIRST to prevent iframe reloads
    setChatInProgress(true);
    
    // Capture current context for this message
    const messageContext = { template: selectedTemplate, model: selectedModel };
    
    // Append user message and persist immediately using the latest messagesRef
    const baseMessages = messagesRef.current || [];
    const messagesWithUser = [...baseMessages, userMsgObj];
    setMessages(messagesWithUser);
    messagesRef.current = messagesWithUser;
    await saveMessagesToStorage(messagesWithUser, messageContext.template, messageContext.model, threadIdRef.current, getAuthHeaders);
    
    // Clear input immediately for better UX
    setDraft('');
    removeImage();
    removeFile();
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setIsLoading(true);

    try {
      // Prepare image data
      let imageBase64 = null;
      let imageMimeType = null;
      if (imageToSend) {
        imageBase64 = await convertImageToBase64(imageToSend);
        imageMimeType = imageToSend.type;
      }
      
      const requestBody = { 
        message: userMessage,
        thread_id: threadIdRef.current,
        template: selectedTemplate,
        model: selectedModel
      };
      
      if (imageBase64) {
        requestBody.image_base64 = imageBase64;
        requestBody.image_mime_type = imageMimeType;
      }
      
      if (filePathToSend) {
        requestBody.uploaded_file_path = filePathToSend;
      }
      
      const authHeaders = getAuthHeaders ? await getAuthHeaders() : {};
      const response = await fetch(`${API_URL}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from server');
      }

      const data = await response.json();
      console.log('AI Response received:', data.response?.substring(0, 100));
      
      // Validate context hasn't changed during async operation
      if (messageContext.template !== selectedTemplate || 
          messageContext.model !== selectedModel) {
        console.log(`[ChatAdvisor]  Context changed during async operation, discarding response. Original: ${messageContext.template}/${messageContext.model}, Current: ${selectedTemplate}/${selectedModel}`);
        setChatInProgress(false);
        return;
      }
      
      const advisorMessage = { role: 'advisor', text: data.response };
      
      // Append assistant message and persist
      const afterAiMessages = [...messagesRef.current, advisorMessage];
      console.log('AI Response: Saving to backend, total messages:', afterAiMessages.length);
      console.log('AI Response preview:', advisorMessage.text.substring(0, 150));
      setMessages(afterAiMessages);
      messagesRef.current = afterAiMessages;
      await saveMessagesToStorage(afterAiMessages, messageContext.template, messageContext.model, threadIdRef.current, getAuthHeaders);
      
      // Use requestAnimationFrame to ensure DOM update happens before clearing flag
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double RAF ensures the message is rendered in the DOM
          // Then wait a bit more before allowing iframe reloads
          setTimeout(() => {
            if (isMountedRef.current) {
              setChatInProgress(false);
              console.log(' Chat completed, iframe reloads now allowed');
            }
          }, 3000); // 3 second delay after render to ensure message is fully visible and saved
        });
      });
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { 
        role: 'advisor', 
        text: 'Sorry, I encountered an error. Please try again.' 
      };
      
      setMessages((prev) => {
        // Check if context is still valid
        if (messageContext.template !== selectedTemplate || 
            messageContext.model !== selectedModel) {
          console.log(`[ChatAdvisor]  Context changed during error handling, skipping save`);
          return prev;
        }
        
        const updated = [...prev, errorMessage];
        // Save immediately to backend with validated context
        saveMessagesToStorage(updated, messageContext.template, messageContext.model, threadIdRef.current, getAuthHeaders);
        
        // Clear flag after a delay on error too
        setTimeout(() => {
          setChatInProgress(false);
        }, 1000);
        
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const LoadingDots = () => (
    <div className="loading-dots">
      <span></span>
      <span></span>
      <span></span>
    </div>
  );

  const clearChat = async () => {
    if (window.confirm('Are you sure you want to clear the chat history for this template and model?')) {
      const defaultMessage = [{ role: 'advisor', text: 'How can I help with today\'s huddle board?' }];
      setMessages(defaultMessage);
      messagesRef.current = defaultMessage;
      // Reset thread_id for this template/model
      threadIdRef.current = await getThreadId(selectedTemplate, selectedModel);
      // Save default state to backend
      await saveMessagesToStorage(defaultMessage, selectedTemplate, selectedModel, threadIdRef.current, getAuthHeaders);
    }
  };

  return (
    <div className="chat-panel">
      <div className="chat-header">
      </div>
      <div className="chat-body" ref={chatBodyRef}>
        {!isOwner && (
          <div className="chat-bubble advisor" style={{
            backgroundColor: '#fef3c7',
            border: '1px solid #fbbf24',
            color: '#92400e',
            padding: '16px',
            marginBottom: '12px'
          }}>
            <strong>Lock: This board is owned by another user.</strong>
            <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
              You can view the board and chat history, but you cannot make edits. 
              Click the "Copy Board" button above to create your own editable version.
            </p>
          </div>
        )}
        {messages.map((m, idx) => (
          <div key={idx} className={`chat-bubble ${m.role}`}>
            {m.image && (
              <img src={m.image} alt="User uploaded" className="chat-message-image" />
            )}
            {m.file && (
              <div style={{ 
                padding: '8px 12px', 
                marginBottom: '8px', 
                background: 'var(--bg-secondary)', 
                borderRadius: '6px',
                fontSize: '13px',
                color: 'var(--text-primary)'
              }}>
                {m.file}
              </div>
            )}
            {m.role === 'advisor' ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>
            ) : (
              <span>{m.text}</span>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="chat-bubble advisor loading-bubble">
            <LoadingDots />
          </div>
        )}
      </div>
      <div className="chat-input-container">
        {imagePreview && (
          <div className="image-preview-container">
            <img src={imagePreview} alt="Preview" className="image-preview" />
            <button onClick={removeImage} className="remove-image-button chat-icon-button" title="Remove image">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span className="sr-only">Remove image</span>
            </button>
          </div>
        )}
        {selectedFile && (
          <div className="file-preview-container" style={{ 
            padding: '8px 12px', 
            margin: '8px 0', 
            background: 'var(--bg-secondary)', 
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px'
          }}>
            <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
              File: {selectedFile.name}
            </span>
            <button 
              onClick={removeFile} 
              className="remove-image-button chat-icon-button" 
              title="Remove file"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span className="sr-only">Remove file</span>
            </button>
          </div>
        )}
        <div className="chat-input-row">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
            disabled={isLoading}
          />
          <input
            type="file"
            ref={csvExcelInputRef}
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={isLoading}
          />
          <div className="attachment-menu-wrapper" ref={attachmentMenuRef}>
            <button
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              className="attachment-button chat-icon-button"
              title="Add attachment"
              disabled={isLoading || !isOwner}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 1 1 4.95 4.95l-8.49 8.49a2 2 0 1 1-2.83-2.83l7.78-7.78"></path>
              </svg>
              <span className="sr-only">Add attachment</span>
            </button>
            {showAttachmentMenu && (
              <div className="attachment-menu">
                <button
                  type="button"
                  className="attachment-menu-item"
                  onClick={() => {
                    setShowAttachmentMenu(false);
                    fileInputRef.current?.click();
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                  <span>Image (PNG/JPG)</span>
                </button>
                <button
                  type="button"
                  className="attachment-menu-item"
                  onClick={() => {
                    setShowAttachmentMenu(false);
                    csvExcelInputRef.current?.click();
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="8" y1="13" x2="16" y2="13"></line>
                    <line x1="8" y1="17" x2="16" y2="17"></line>
                  </svg>
                  <span>CSV / Excel</span>
                </button>
              </div>
            )}
          </div>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={isOwner ? "Ask a question or leave a note..." : "Copy this board to edit..."}
            disabled={isLoading || !isOwner}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && isOwner) {
                e.preventDefault();
                sendMessage();
              }
            }}
            rows={1}
            className="chat-textarea"
          />
        <button 
          onClick={sendMessage} 
            disabled={isLoading || !isOwner || (!draft.trim() && !selectedImage && !uploadedFilePath)}
          className="chat-send-button"
            title={isOwner ? "Send message (Enter)" : "Copy board to edit"}
        >
          {isLoading ? (
              <span className="button-loading-dots">
                <span></span><span></span><span></span>
              </span>
          ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
          )}
          <span className="sr-only">Send message</span>
        </button>
        </div>
        <div className="chat-model-row">
          <label className="chat-model-label" htmlFor="chat-model-select">Model</label>
          <select
            id="chat-model-select"
            value={selectedModel}
            onChange={(e) => handleModelChange(e.target.value)}
            className="model-selector"
            title="Select AI Model"
            disabled={isLoading || !isOwner}
          >
            <option value="openai">GPT 4.1</option>
            <option value="gemini">Google Gemini 3 Pro</option>
            <option value="gpt5">GPT 5.1</option>
          </select>
          <button 
            onClick={clearChat}
            className="clear-chat-button chat-icon-button"
            title="Clear chat history"
            disabled={!isOwner}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
              <path d="M10 11v6"></path>
              <path d="M14 11v6"></path>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"></path>
            </svg>
            <span className="sr-only">Clear chat</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// Memoize to reduce parent-driven re-renders.
// Internal state changes still trigger re-renders as expected.
export default memo(ChatAdvisor);


